import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/api-client';
import { Booking, ContactInfo, CommunicationRecord } from '@/types';
import { Auth } from 'aws-amplify';

// Enhanced booking interface with guest completion status
export interface BookingWithGuestStatus extends Omit<Booking, 'guests'> {
  guestCompletionStatus: {
    total: number;
    completed: number;
    pending: number;
  };
  guests: GuestSummary[];
}

export interface GuestSummary {
  guestId?: string;
  guestName?: string;
  contactInfo?: ContactInfo;
  checkInStatus: 'pending' | 'in_progress' | 'completed';
  documentsVerified: boolean;
  completedAt?: string;
}

export interface GuestDetail extends GuestSummary {
  checkInRecord?: any;
  communicationHistory: any[];
  documentPreviews?: {
    idFront?: string;
    idBack?: string;
  };
}

// Communication management types
export interface CommunicationHistoryResponse {
  communications: CommunicationRecord[];
  total: number;
}

export interface SendManualReminderRequest {
  bookingId: string;
  recipientType: 'all' | 'pending' | 'specific';
  specificGuestId?: string;
  channel: 'email' | 'whatsapp' | 'both';
  subject?: string;
  message: string;
}

export interface SendManualReminderResponse {
  message: string;
  sentCount: number;
  sentAt: string;
}

export interface BulkCommunicationRequest {
  bookingId: string;
  channel: 'email' | 'whatsapp' | 'both';
  subject?: string;
  message: string;
  includeCompleted: boolean;
}

export interface BulkCommunicationResponse {
  message: string;
  sentCount: number;
  sentAt: string;
}

// Query keys
export const guestKeys = {
  all: ['guests'] as const,
  bookingsWithStatus: () => [...guestKeys.all, 'bookings-with-status'] as const,
  bookingsWithStatusFiltered: (filters: Record<string, any>) => [...guestKeys.bookingsWithStatus(), { filters }] as const,
  details: () => [...guestKeys.all, 'details'] as const,
  detail: (bookingId: string) => [...guestKeys.details(), bookingId] as const,
  communication: () => [...guestKeys.all, 'communication'] as const,
  communicationHistory: (bookingId: string) => [...guestKeys.communication(), 'history', bookingId] as const,
};

// Get bookings with guest completion status
export const useBookingsWithGuestStatus = (filters?: { 
  status?: string; 
  listingId?: string; 
  startDate?: string; 
  endDate?: string; 
}, enabled: boolean = true) => {
  return useQuery({
    queryKey: guestKeys.bookingsWithStatusFiltered(filters || {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.listingId) params.append('listingId', filters.listingId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      
      const endpoint = `/guests/bookings-with-status${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get<{
        bookings: BookingWithGuestStatus[];
        total: number;
      }>(endpoint);
      return response.data;
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds (guest status changes frequently)
    cacheTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Get detailed guest information for a booking
export const useGuestDetails = (bookingId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: guestKeys.detail(bookingId),
    queryFn: async () => {
      try {
        const response = await apiClient.get<{
          bookingId: string;
          guests: GuestDetail[];
          total: number;
          accessDenied?: boolean;
        }>(`/guests/${bookingId}/details`);
        return response.data;
      } catch (error: any) {
        // Handle specific authorization errors gracefully
        if (error?.response?.status === 403) {
          console.warn(`Access denied for booking ${bookingId} guest details`);
          return {
            bookingId,
            guests: [],
            total: 0,
            accessDenied: true
          };
        }
        throw error;
      }
    },
    enabled: !!bookingId && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    cacheTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 403 errors
      if (error?.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Send reminder to guests
export const useSendGuestReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, guestId }: { 
      bookingId: string; 
      guestId?: string; 
    }) => {
      const response = await apiClient.post<{
        message: string;
        sentAt: string;
      }>(`/guests/${bookingId}/reminder`, { guestId });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate guest details to refresh status
      queryClient.invalidateQueries({ queryKey: guestKeys.detail(variables.bookingId) });
      queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() });
    },
  });
};

// Export guest data
export const useExportGuestData = () => {
  return useMutation({
    mutationFn: async ({ bookingId, format }: { 
      bookingId: string; 
      format: 'csv' | 'pdf'; 
    }) => {
      // Get auth token using Amplify
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/guests/${bookingId}/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ format }),
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      if (format === 'csv') {
        // For CSV, get the data as text and create a download
        const csvData = await response.text();
        const blob = new Blob([csvData], { type: 'text/csv' });
        const downloadUrl = URL.createObjectURL(blob);
        
        // Trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `guests_${bookingId}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
        
        return {
          message: 'CSV export completed',
          downloadUrl: downloadUrl,
          format,
          generatedAt: new Date().toISOString(),
        };
      } else {
        // For PDF or other formats, expect JSON response with downloadUrl
        const data = await response.json();
        return data;
      }
    },
  });
};

// Get communication history for a booking
export const useCommunicationHistory = (bookingId: string) => {
  return useQuery({
    queryKey: guestKeys.communicationHistory(bookingId),
    queryFn: async () => {
      const response = await apiClient.get<any>(`/bookings/${bookingId}/communications`);
      
      // Handle different response structures (same logic as BookingConfirmationService)
      const data = response.data || response;
      
      // The backend returns { communicationLog: [...] }
      if (data.communicationLog && Array.isArray(data.communicationLog)) {
        return data.communicationLog;
      } else if (data.communications && Array.isArray(data.communications)) {
        return data.communications;
      } else if (Array.isArray(data)) {
        return data;
      } else {
        console.warn('Unexpected communication history response structure:', data);
        return [];
      }
    },
    enabled: !!bookingId,
    staleTime: Infinity, // Never consider data stale
    refetchOnWindowFocus: false, // No refetch on window focus
    refetchOnMount: false, // No refetch on mount if data exists
    refetchOnReconnect: false, // No refetch on network reconnect
    refetchInterval: false, // No polling
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

// Send manual reminder
export const useSendManualReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SendManualReminderRequest) => {
      const response = await apiClient.post<SendManualReminderResponse>(
        `/guests/${request.bookingId}/manual-reminder`, 
        request
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: guestKeys.detail(variables.bookingId) });
      queryClient.invalidateQueries({ queryKey: guestKeys.communicationHistory(variables.bookingId) });
      queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() });
    },
  });
};

// Send bulk communication
export const useBulkCommunication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: BulkCommunicationRequest) => {
      const response = await apiClient.post<BulkCommunicationResponse>(
        `/guests/${request.bookingId}/bulk-communication`, 
        request
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: guestKeys.detail(variables.bookingId) });
      queryClient.invalidateQueries({ queryKey: guestKeys.communicationHistory(variables.bookingId) });
      queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() });
    },
  });
};